<template>
  <div class="">
    <div class="flex-1 p-6">
      <div class="space-y-6">
        <div class="flex items-center justify-between mt-5">
          <div class="flex items-center gap-2">
            <!-- <ArrowLeftIcon class="w-6 h-6" /> -->
            <h1 class="text-2xl font-medium text-gray-800">
              {{ campaignName || "Test de smishing" }}
            </h1>
          </div>
          <Tag
            :value="getStatusLabel(campaignStatus)"
            severity="success"
            class="px-4 py-1 rounded-md"
          />
        </div>
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-2">
          <div
            class="bg-white p-4 rounded shadow-md flex items-start justify-between"
          >
            <div>
              <div class="text-sm text-cyan-600 font-semibold">
                {{ t("audits.type") }}
              </div>

              <div class="text-gray-700 mt-1">
                {{ t("audits.audit_smihing") }}
              </div>
            </div>
            <ClipboardList class="text-cyan-600 w-12 h-12" />
          </div>

          <div
            class="bg-white p-4 rounded shadow-md flex items-start justify-between"
          >
            <div>
              <div class="text-sm text-cyan-600 font-semibold">
                {{ t("audits.users") }}
              </div>

              <div class="text-gray-700 mt-1">{{ userCount }}</div>
            </div>
            <Users2 class="text-cyan-600 w-8 h-8" />
          </div>

          <div
            class="bg-white p-4 rounded shadow-md flex items-start justify-between"
          >
            <div>
              <div class="text-sm text-cyan-600 font-semibold">
                {{ t("audits.start_date") }}
              </div>
              <div class="text-gray-700 mt-1">{{ formatDate(launchDate) }}</div>
            </div>
            <CalendarPlus class="text-cyan-600 w-6 h-6" />
          </div>

          <div
            class="bg-white p-4 rounded shadow-md flex items-start justify-between"
          >
            <div>
              <div class="text-sm text-cyan-600 font-semibold">
                {{ t("audits.end_date") }}
              </div>
              <div class="text-gray-700 mt-1">
                {{ formatDate(closingDate) }}
              </div>
            </div>
            <CalendarX class="text-cyan-600 w-6 h-6" />
          </div>
        </div>

        <!-- Canvas-based Charts Section -->
        <div class="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <div
            v-for="(item, index) in chartDataList"
            :key="index"
            class="bg-white p-4 rounded shadow-md flex flex-col items-center justify-center"
          >
            <!-- Custom Canvas Circular Progress -->
            <div class="relative w-32 h-32 flex items-center justify-center">
              <canvas
                :ref="(el) => setCanvasRef(el, index)"
                width="128"
                height="128"
                class="w-32 h-32"
              ></canvas>
              <!-- Percentage text in center -->
              <div class="absolute inset-0 flex items-center justify-center">
                <span class="text-2xl font-bold text-gray-800">{{
                  item.value
                }}</span>
              </div>
            </div>
            <!-- Label below chart -->
            <div class="mt-3 text-center text-sm font-medium text-gray-700">
              {{ item.label }}
            </div>
          </div>
        </div>

        <div class="bg-white p-6 rounded-lg shadow">
          <div class="flex justify-between items-center">
            <h2 class="text-xl font-medium">{{ t("audits.detail_title") }}</h2>
            <Button
              :label="
                isDownloading
                  ? t('audits.is_downloading')
                  : t('audits.download_report')
              "
              :icon="isDownloading ? 'pi pi-spin pi-spinner' : 'pi pi-download'"
              :class="[
                'p-button-rounded',
                canDownloadReport
                  ? 'p-button-primary'
                  : 'p-button-secondary p-button-outlined',
              ]"
              @click="downloadReport"
              :disabled="!canDownloadReport || isDownloading"
              :tooltip="
                !canDownloadReport
                  ? `${t('audits.available_after')} ${formatDate(closingDate)}`
                  : t('audits.download_report_tip')
              "
              tooltipPosition="top"
            />
          </div>

          <DataTable :value="detailsData" stripedRows>
            <!-- <Column header="">
              <template #body>
                <component :is="SquareIcon" class="w-4 h-4" />
              </template>
            </Column> -->
            <Column
              field="name"
              :header="t('audits.campaign_table_name')"
            ></Column>
            <Column
              field="template_name"
              :header="t('audits.campaign_table_template')"
            >
              <template #body="slotProps">
                <div class="flex align-items-center">
                  <div
                    class="mr-2 bg-gray-100 p-2 rounded-md"
                    style="
                      width: 40px;
                      height: 40px;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                    "
                  >
                    <i class="pi pi-file-pdf text-cyan-600 text-xl"></i>
                  </div>
                  <div>
                    <p class="mb-0 font-medium">
                      {{ slotProps.data.template_name }}
                    </p>
                  </div>
                </div>
              </template>
            </Column>
            <Column
              field="group"
              :header="t('audits.campaign_table_group')"
            ></Column>
            <Column field="status" :header="t('audits.campaign_table_status')">
              <template #body="slotProps">
                <Tag
                  :value="slotProps.data.status"
                  severity="success"
                  class="px-3 py-1 rounded text-sm"
                />
              </template>
            </Column>
          </DataTable>
        </div>

        <div class="bg-white p-6 rounded-lg shadow">
          <h2 class="text-xl font-medium mb-6">
            {{ t("audits.details_users") }}
          </h2>

          <DataTable
            :value="usersData"
            stripedRows
            expandableRows
            @row-expand="onRowExpand"
            @row-collapse="onRowCollapse"
            v-model:expandedRows="expandedRows"
          >
            <Column expander style="width: 3rem" />
            <Column
              field="name"
              :header="t('audits.details_table_name')"
            ></Column>
            <Column
              field="email"
              :header="t('audits.details_table_email')"
            ></Column>
            <Column
              field="phone"
              :header="t('audits.details_table_phone')"
            ></Column>
            <Column field="status" :header="t('audits.details_table_status')">
              <template #body="slotProps">
                <Tag
                  :value="slotProps.data.status"
                  :severity="getUserStatusSeverity(slotProps.data.statusColor)"
                  class="px-3 py-1 rounded text-sm"
                />
              </template>
            </Column>
            <template #expansion="slotProps">
              <div class="p-4 bg-gray-50 rounded-lg">
                <Card class="">
                  <template #header>
                    <div class="flex align-items-center p-3 bg-cyan-50">
                      <Avatar
                        :image="slotProps.data.avatar || null"
                        icon="pi pi-user"
                        size="large"
                        shape="circle"
                        class="mr-3"
                      />
                      <div>
                        <h3 class="text-xl font-semibold mb-1">
                          {{ slotProps.data.name }}
                        </h3>
                        <p class="text-gray-600 mb-0">
                          {{ slotProps.data.email }}
                        </p>
                        <p
                          class="text-gray-500 mb-0"
                          v-if="slotProps.data.phone"
                        >
                          {{ slotProps.data.phone }}
                        </p>
                      </div>
                    </div>
                  </template>

                  <template #title>
                    <div class="flex align-items-center">
                      <i class="pi pi-chart-line mr-2 text-cyan-600"></i>
                      <h4 class="text-lg font-medium mb-0">
                        {{ t("audits.details_activity") }}
                      </h4>
                    </div>
                  </template>

                  <template #content>
                    <Timeline
                      :value="getUserTimelineEvents(slotProps.data)"
                      :layout="'horizontal'"
                      class="mt-3 w-full"
                      align="top"
                    >
                      <!-- <template #content="timelineProps">
                        <Card class="mb-3 w-fit timeline-card" :pt="{
                          root: { class: timelineProps.item.status ? 'border-left-3 border-' + timelineProps.item.severity : '' }
                        }">
                          <template #title>
                            <div class="flex align-items-center">
                              <i :class="'pi ' + timelineProps.item.icon + ' mr-2 text-' + timelineProps.item.severity"></i>
                              <span class="whitespace-nowrap">{{ timelineProps.item.label }}</span>
                            </div>
                          </template>
                          <template #content>
                            <p class="mb-0">{{ timelineProps.item.status ? timelineProps.item.date : 'Non effectué' }}</p>
                          </template>
                        </Card>
                      </template> -->
                      <template #marker="timelineProps">
                        <div>
                          <div
                            class="flex align-items-center justify-center p-3 rounded-full"
                            :style="{
                              background: timelineProps.item.background,
                            }"
                          >
                            <i
                              :class="
                                'pi ' +
                                timelineProps.item.icon +
                                ' text-' +
                                timelineProps.item.severity
                              "
                            ></i>
                          </div>
                        </div>
                      </template>
                      <template #content="timelineProps">
                        <small class="text-surface-500">{{
                          timelineProps.item.status
                            ? `Effectué le ${timelineProps.item.date} (${timelineProps.item.count} fois)`
                            : "Non effectué"
                        }}</small>
                      </template>
                      <template #opposite="timelineProps">
                        <span class="whitespace-nowrap">{{
                          timelineProps.item.label
                        }}</span>
                      </template>
                    </Timeline>

                    <!-- Events Table Section -->
                    <div class="mt-8 pt-6 border-t border-gray-200">
                      <h5 class="text-lg font-medium mb-4 flex align-items-center">
                        <i class="pi pi-list mr-2 text-cyan-600"></i>
                        {{ t("audits.events_detail") }}
                      </h5>
                      <DataTable
                        :value="getUserEvents(slotProps.data)"
                        stripedRows
                        class="p-datatable-sm"
                      >
                        <Column
                          field="event_type"
                          :header="t('audits.event_type')"
                        >
                          <template #body="eventProps">
                            <Tag
                              :value="getEventTypeLabel(eventProps.data.event_type)"
                              :severity="getEventTypeSeverity(eventProps.data.event_type)"
                              class="px-2 py-1 text-xs"
                            />
                          </template>
                        </Column>
                        <Column
                          field="event_date"
                          :header="t('audits.event_date')"
                        >
                          <template #body="eventProps">
                            {{ formatDateToLocal(eventProps.data.event_date, true) }}
                          </template>
                        </Column>
                        <Column
                          field="ip_address"
                          :header="t('audits.ip_address')"
                        >
                          <template #body="eventProps">
                            <span>{{ eventProps.data.ip_address || "N/A" }}</span>
                          </template>
                        </Column>
                        <Column
                          field="latitude"
                          :header="t('audits.location')"
                        >
                          <template #body="eventProps">
                            <span v-if="eventProps.data.latitude && eventProps.data.longitude">
                              {{ eventProps.data.latitude }}, {{ eventProps.data.longitude }}
                            </span>
                            <span v-else>N/A</span>
                          </template>
                        </Column>
                        <Column
                          field="user_agent"
                          :header="t('audits.user_agent')"
                        >
                          <template #body="eventProps">
                            <span
                              v-if="eventProps.data.user_agent"
                              class="text-xs text-gray-600 truncate"
                              :title="eventProps.data.user_agent"
                            >
                              {{ truncateUserAgent(eventProps.data.user_agent) }}
                            </span>
                            <span v-else>N/A</span>
                          </template>
                        </Column>
                      </DataTable>
                    </div>
                  </template>
                </Card>
              </div>
            </template>
          </DataTable>
        </div>

        <!-- Supprimer le Dialog qui n'est plus nécessaire -->
        <!-- <Dialog v-model:visible="userDetailModalVisible" header="Détail Utilisateur" :style="{width: '60vw'}" modal> ... </Dialog> -->
        <Dialog
          v-model:visible="userDetailModalVisible"
          header="Détail Utilisateur"
          :style="{ width: '60vw' }"
          modal
        >
          <div v-if="currentUserDetail" class="p-4">
            <Card>
              <template #header>
                <div class="flex align-items-center p-3 bg-cyan-50">
                  <Avatar
                    :image="currentUserDetail.avatar || null"
                    icon="pi pi-user"
                    size="large"
                    shape="circle"
                    class="mr-3"
                  />
                  <div>
                    <h3 class="text-xl font-semibold mb-1">
                      {{ currentUserDetail.name }}
                    </h3>
                    <p class="text-gray-600 mb-0">
                      {{ currentUserDetail.email }}
                    </p>
                    <p
                      class="text-gray-500 mb-0"
                      v-if="currentUserDetail.phone"
                    >
                      {{ currentUserDetail.phone }}
                    </p>
                  </div>
                </div>
              </template>

              <template #title>
                <div class="flex align-items-center">
                  <i class="pi pi-chart-line mr-2 text-cyan-600"></i>
                  <h4 class="text-lg font-medium mb-0">
                    Activité de la campagne
                  </h4>
                </div>
              </template>

              <template #content>
                <Timeline :value="userTimelineEvents" class="mt-3">
                  <template #content="slotProps">
                    <Card
                      class="mb-3"
                      :pt="{
                        root: {
                          class: slotProps.item.status
                            ? 'border-left-3 border-' + slotProps.item.severity
                            : '',
                        },
                      }"
                    >
                      <template #title>
                        <div class="flex align-items-center">
                          <i
                            :class="
                              'pi ' +
                              slotProps.item.icon +
                              ' mr-2 text-' +
                              slotProps.item.severity
                            "
                          ></i>
                          <span>{{ slotProps.item.label }}</span>
                        </div>
                      </template>
                      <template #content>
                        <p class="mb-0">
                          {{
                            slotProps.item.status
                              ? slotProps.item.date
                              : "Non effectué"
                          }}
                        </p>
                      </template>
                    </Card>
                  </template>
                </Timeline>
              </template>
            </Card>
          </div>
        </Dialog>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from "vue";
import Button from "primevue/button";
import { useRoute } from "vue-router";
import Tag from "primevue/tag";
//import Chart from 'primevue/chart';
import Dialog from "primevue/dialog";
import Timeline from "primevue/timeline";
import Card from "primevue/card";
import Avatar from "primevue/avatar";
//import Tooltip from 'primevue/tooltip';
import {
  ClipboardList,
  Users2,
  CalendarPlus,
  CalendarX,
} from "lucide-vue-next";
import DataTable from "primevue/datatable";
import { fetchGroupById } from "@/services/groupService";
import Column from "primevue/column";
import {
  fetchPhishingCampaignById,
  //fetchSmishingCampaignRecords,
  //fetchSmishingCampaignAnalytics,
  generatePhishingCampaignReport,
} from "@/services/phishingService";
import Swal from "sweetalert2";
import { useI18n } from "vue-i18n";

// Lucide Icons for Vue
import //ArrowLeft,
//Square,
"lucide-vue-next";

const { t } = useI18n();
const route = useRoute();
const auditId = route.params.id;

// Rename imported icons for easier use in template
//const ArrowLeftIcon = ArrowLeft;
//const SquareIcon = Square;

// State variables
const campaignName = ref("");
const campaignStatus = ref("");
const userCount = ref(0);
const launchDate = ref(null);
const closingDate = ref(null);
const detailsData = ref([]);
const usersData = ref([]);
const isLoading = ref(true);
const userDetailModalVisible = ref(false);
const currentUserDetail = ref(null);
const currentUserDetailActions = ref({
  email_sent: [false, ""],
  email_opened: [false, ""],
  clicked_link: [false, ""],
  submitted_data: [false, ""],
});

// Analytics data
const emailStats = ref({
  total_sent: 0,
  total_opened: 0,
  total_clicked: 0,
  total_viewed: 0,
  total_reported: 0,
});

// Chart data
const chartDataList = computed(() => [
  {
    label: `Emails envoyés (${emailStats.value.total_sent})`,
    value: emailStats.value.total_sent,
    color: "#00C2CB",
    total: emailStats.value.total,
  },
  {
    label: `Emails ouverts (${emailStats.value.total_opened})`,
    value: emailStats.value.total_opened,
    color: "#FFA726",
    total: emailStats.value.total,
  },
  {
    label: `Liens cliqués (${emailStats.value.total_clicked})`,
    value: emailStats.value.total_clicked,
    color: "#66BB6A",
    total: emailStats.value.total,
  },
  {
    label: `Données soumises (${emailStats.value.total_viewed})`,
    value: emailStats.value.total_viewed,
    color: "#263238",
    total: emailStats.value.total,
  },
  {
    label: `Emails signalés (${emailStats.value.total_reported || 0})`,
    value: emailStats.value.total_reported || 0,
    color: "#EF5350",
    total: emailStats.value.total,
  },
]);

/*
const chartOptions = {
  cutout: '75%',
  responsive: true,
  maintainAspectRatio: true,
  plugins: {
    legend: { display: false },
    tooltip: { enabled: false }
  },
  elements: {
    arc: {
      borderWidth: 0,
      borderRadius: 5
    }
  },
  animation: {
    animateRotate: true,
    animateScale: true
  }
};
// Mise à jour de la fonction getChartData
const getChartData = (value, color) => ({
  labels: ['Utilisé', 'Restant'],
  datasets: [
    {
      data: [value, 100 - value],
      backgroundColor: [color, '#F3F4F6'],
      hoverBackgroundColor: [color, '#E5E7EB'],
      borderWidth: 0,
      borderRadius: 5
    }
  ]
});
*/

// Computed properties
const canDownloadReport = computed(() => {
  if (!closingDate.value) return false;
  const now = new Date();
  const endDate = new Date(closingDate.value);
  return now > endDate;
});

// Helper functions
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString("fr-FR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

const formatDateToLocal = (dateString, includeTime = false) => {
  if (!dateString) return "N/A";
  const options = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  };

  if (includeTime) {
    options.hour = "2-digit";
    options.minute = "2-digit";
    options.second = "2-digit";
  }

  return new Date(dateString).toLocaleDateString("fr-FR", options);
};

const getUserStatusSeverity = (color) => {
  //return color === 'success' ? 'success' : 'danger';
  return color;
};

const getStatusLabel = (status) => {
  if (!status) return "N/A";

  switch (status.toLowerCase()) {
    case "created":
      return t("audits.status.created");
    case "canceled":
      return t("audits.status.canceled");
    case "closed":
      return t("audits.status.closed");
    case "completed":
      return t("audits.status.completed");
    case "queued":
      return t("audits.status.queued");
    case "in_progress":
      return t("audits.status.in_progress");
    default:
      return t("audits.status.default");
  }
};

/*const getChartData = (value, color) => ({
  labels: ['Used', 'Remaining'],
  datasets: [
    {
      data: [value, 100 - value],
      backgroundColor: [color, '#F3F4F6'],
      borderWidth: 0
    }
  ]
});*/

// Methods
const fetchCampaignDetails = async () => {
  try {
    isLoading.value = true;

    // Fetch campaign details
    const campaignData = await fetchPhishingCampaignById(auditId);
    if (!campaignData) throw new Error("Failed to fetch campaign details");

    // Set campaign data
    const detail = campaignData.detail;
    campaignName.value = detail.name;
    campaignStatus.value = detail.status;
    launchDate.value = detail.launch_date;
    closingDate.value = detail.end_date || detail.send_by_date;

    // Set details table data
    detailsData.value = [
      {
        id: detail.id,
        name: detail.name,
        template_name: detail.template_details?.name || "N/A",
        group: (detail.groups?.[0]?.name || "N/A").split("_")[0] || "N/A",
        status: getStatusLabel(detail.status),
      },
    ];

    // Fetch group users
    if (detail.groups && detail.groups.length > 0) {
      const groupId = detail.groups[0];
      console.log("Id du groupe ",groupId)
      const groupData = await fetchGroupById(groupId);

      if (groupData && groupData.user_detail) {
        userCount.value = groupData.user_detail.length;

        // Fetch campaign records
        //const records = await fetchSmishingCampaignRecords(auditId);
        const records = detail.statistic;
        // Process users data
        if (Array.isArray(records) && records.length > 0) {
          // Map users with their campaign interaction data
          usersData.value = records.map((record) => {
            let user = groupData.user_detail?.find(
              (user) => user.email == record.email
            );
            let status = "Email non envoyé";
            let statusColor = "danger";

            if (record.email_sent_count > 0) {
              status = `${t("audits.events_status.sent")}`;
              statusColor = "success";

              if (record.opened_count > 0) {
                status = `${t("audits.events_status.opened")}`;
                statusColor = "warn";
              }

              if (record.clicked_count > 0) {
                status = `${t("audits.events_status.clicked")}`;
                statusColor = "warn";
              }

              if (record.submitted_data_count > 0) {
                status = `${t("audits.events_status.submitted")}`;
                statusColor = "danger";
              }
            }

            return {
              id: record.id,
              name:
                `${record.last_name || ""} ${record.first_name || ""}`.trim() ||
                "N/A",
              email: record.email || user?.email || "N/A",
              phone: record.phone_number || user?.phone || "N/A",
              status,
              statusColor,
              rawData: record,
            };
          });
        } else {
          // If no campaign records, use group users data
          usersData.value = groupData.user_detail.map((user) => ({
            id: user.id,
            name:
              `${user.lastname || ""} ${user.firstname || ""}`.trim() || "N/A",
            email: user.email || "N/A",
            phone: user.phone || "N/A",
            status: "En attente",
            statusColor: "warn",
            rawData: user,
          }));
        }
      }
    }

    // Fetch campaign analytics
    //const analytics = await fetchSmishingCampaignAnalytics(auditId);
    if (detail) {
      emailStats.value = {
        total_sent: detail.sent_phishing || 0,
        total_opened: detail.opened_phishing || 0,
        total_clicked: detail.clicked_phishing || 0,
        total_viewed: detail.submitted_data_phishing || 0,
        total_reported: detail.email_reported_phishing || 0,
        total: detail.total_phishing || 0,
      };
    }
  } catch (error) {
    console.error("Error fetching campaign details:", error);
  } finally {
    isLoading.value = false;
  }
};

/*const showUserDetail = (user) => {
  currentUserDetail.value = user;
  processUserDetailStatus(user.rawData);
  userDetailModalVisible.value = true;
};*/

const processUserDetailStatus = (userData) => {
  currentUserDetailActions.value = {
    email_sent: [false, ""],
    email_opened: [false, ""],
    clicked_link: [false, ""],
    submitted_data: [false, ""],
  };

  if (!userData) return;

  // Check if the Email was sent
  if (userData.email_sent_phishing) {
    currentUserDetailActions.value.email_sent = [
      true,
      formatDateToLocal(userData.email_sent_phishing_date, true),
    ];
  }

  // Check if the Email was opened
  if (userData.opened_phishing) {
    currentUserDetailActions.value.email_opened = [
      true,
      formatDateToLocal(
        userData.opened_phishing_date || userData.email_sent_phishing_date,
        true
      ),
    ];
  }

  // Check if the link was clicked
  if (userData.clicked_phishing) {
    currentUserDetailActions.value.clicked_link = [
      true,
      formatDateToLocal(
        userData.clicked_phishing_date || userData.opened_phishing_date,
        true
      ),
    ];
  }

  // Check if data was submitted (viewed)
  if (userData.submitted_data_phishing) {
    currentUserDetailActions.value.submitted_data = [
      true,
      formatDateToLocal(
        userData.submitted_data_phishing_date || userData.clicked_phishing_date,
        true
      ),
    ];
  }
};

// Ajout de l'état de téléchargement
const isDownloading = ref(false);

// Mise à jour de la fonction downloadReport
const downloadReport = async () => {
  try {
    if (!canDownloadReport.value) {
      Swal.fire({
        title: "Rapport de campagne",
        text: `Votre campagne est toujours en cours. Veuillez patientez jusqu'au ${formatDateToLocal(closingDate.value)}`,
        icon: "warning",
        confirmButtonText: "Fermer",
      });
      return;
    }

    isDownloading.value = true;
    const pdfBlob = await generatePhishingCampaignReport(
      auditId,
      campaignName.value
    );

    if (pdfBlob) {
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${campaignName.value}_rapport.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Afficher un message de succès
      Swal.fire({
        title: "Rapport téléchargé",
        text: "Le rapport a été téléchargé avec succès",
        icon: "success",
        confirmButtonText: "Fermer",
      });
    } else {
      Swal.fire({
        title: "Rapport de campagne",
        text: "Rapport indisponible pour le moment",
        icon: "warning",
        confirmButtonText: "Fermer",
      });
    }
  } catch (error) {
    console.error("Error downloading report:", error);
    Swal.fire({
      title: "Rapport de campagne",
      text: "Rapport indisponible pour le moment",
      icon: "warning",
      confirmButtonText: "Fermer",
    });
  } finally {
    isDownloading.value = false;
  }
};

// Lifecycle hooks
onMounted(async () => {
  await fetchCampaignDetails();
});

// Computed property for user timeline events
const userTimelineEvents = computed(() => {
  if (!currentUserDetailActions.value) return [];

  return [
    {
      label: "Email envoyé",
      date: currentUserDetailActions.value.email_sent[1],
      status: currentUserDetailActions.value.email_sent[0],
      icon: "pi-send",
      severity: "primary",
    },
    {
      label: "Email ouvert",
      date: currentUserDetailActions.value.email_opened[1],
      status: currentUserDetailActions.value.email_opened[0],
      icon: "pi-envelope-open",
      severity: "info",
    },
    {
      label: "Lien cliqué",
      date: currentUserDetailActions.value.clicked_link[1],
      status: currentUserDetailActions.value.clicked_link[0],
      icon: "pi-link",
      severity: "warning",
    },
    {
      label: "Données soumises",
      date: currentUserDetailActions.value.submitted_data[1],
      status: currentUserDetailActions.value.submitted_data[0],
      icon: "pi-check-circle",
      severity: "success",
    },
  ];
});

// Ajouter ces variables pour gérer l'expansion des lignes
const expandedRows = ref([]);

// Fonction pour traiter les événements d'expansion de ligne
const onRowExpand = (event) => {
  processUserDetailStatus(event.data.rawData);
};

const onRowCollapse = () => {
  // Vous pouvez ajouter une logique ici si nécessaire
};

// Fonction pour obtenir les événements de timeline pour un utilisateur spécifique
const getUserTimelineEvents = (userData) => {
  // Traiter les données de l'utilisateur pour la timeline
  const actions = {
    email_sent: [false, "", 0],
    email_opened: [false, "", 0],
    clicked_link: [false, "", 0],
    submitted_data: [false, "", 0],
  };

  if (!userData.rawData || !userData.rawData.events) return [];

  const events = userData.rawData.events

   const findLastEvent = (eventType) => {
    const filtered = events.filter(e => e.event_type === eventType);
    return filtered.length > 0 ? filtered[filtered.length - 1] : null;
  };

  // Check if the Email was sent
  if (userData.rawData.email_sent_count > 0) {
    const event = findLastEvent('email_sent');
    actions.email_sent = [
      true,
      event ? formatDateToLocal(event.event_date, true) : "N/A",
      userData.rawData.email_sent_count
    ];
  }

  // Check if the Email was opened
  if (userData.rawData.opened_count > 0) {
    const event = findLastEvent('email_opened');
    actions.email_opened = [
       true,
      event ? formatDateToLocal(event.event_date, true) : "N/A",
      userData.rawData.opened_count
    ];
  }

  // Check if the link was clicked
  if (userData.rawData.clicked_count > 0) {
    const event = findLastEvent('clicked_link')
    actions.clicked_link = [
     true,
      event ? formatDateToLocal(event.event_date, true) : "N/A",
      userData.rawData.clicked_count
    ];
  }

  // Check if data was submitted (viewed)
  if (userData.rawData.submitted_data_count > 0) {
    const event = findLastEvent('submitted_data');
    actions.submitted_data = [
      true,
      event ? formatDateToLocal(event.event_date, true) : "N/A",
      userData.rawData.submitted_data_count
    ];
  }

  return [
    {
      label: "Email envoyé",
      date: actions.email_sent[1],
      status: actions.email_sent[0],
      count: actions.email_sent[2],
      icon: "pi-send",
      severity: "primary",
      background: "#E6F8F9",
    },
    {
      label: "Email ouvert",
      date: actions.email_opened[1],
      status: actions.email_opened[0],
      count: actions.email_opened[2],
      icon: "pi-eye",
      severity: "info",
      background: "#FEF6EA",
    },
    {
      label: "Lien cliqué",
      date: actions.clicked_link[1],
      status: actions.clicked_link[0],
      count: actions.clicked_link[2],
      icon: "pi-link",
      severity: "warning",
      background: "#FFEAEB",
    },
    {
      label: "Données soumises",
      date: actions.submitted_data[1],
      status: actions.submitted_data[0],
      count: actions.submitted_data[2],
      icon: "pi-check-circle",
      severity: "success",
      background: "#E7F9F0",
    },
  ];
};

// Vous pouvez supprimer ou modifier ces fonctions qui ne sont plus nécessaires
// const showUserDetail = (user) => { ... };
// const processUserDetailStatus = (userData) => { ... };

// Vous pouvez conserver processUserDetailStatus pour mettre à jour currentUserDetailActions
// qui sera utilisé par getUserTimelineEvents

// Canvas refs and methods
const canvasRefs = ref([]);

const setCanvasRef = (el, index) => {
  if (el) {
    canvasRefs.value[index] = el;
    drawCircularProgress(el, chartDataList.value[index]);
  }
};

const drawCircularProgress = (canvas, data) => {
  const ctx = canvas.getContext("2d");
  const centerX = canvas.width / 2;
  const centerY = canvas.height / 2;
  const radius = 45;
  const lineWidth = 8;

  // Clear canvas
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  // Draw background circle
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
  ctx.strokeStyle = "#F3F4F6";
  ctx.lineWidth = lineWidth;
  ctx.stroke();

  // Draw progress arc
  const startAngle = -Math.PI / 2; // Start from top
  const endAngle =
    startAngle +
    (2 * Math.PI * (Math.round((data.value / data.total) * 100) || 0)) / 100;

  ctx.beginPath();
  ctx.arc(centerX, centerY, radius, startAngle, endAngle);
  ctx.strokeStyle = data.color;
  ctx.lineWidth = lineWidth;
  ctx.lineCap = "round";
  ctx.stroke();
};

// Nouvelle méthode pour obtenir les événements d'un utilisateur
const getUserEvents = (userData) => {
  if (!userData || !userData.rawData || !userData.rawData.events) {
    return [];
  }
  
  // Retourner les événements triés par date (plus récents d'abord)
  return [...userData.rawData.events].sort((a, b) => {
    return new Date(b.event_date) - new Date(a.event_date);
  });
};

// Méthode pour obtenir le label du type d'événement
const getEventTypeLabel = (eventType) => {
  const labels = {
    email_sent: t("audits.event_email_sent"),
    email_opened: t("audits.event_email_opened"),
    clicked_link: t("audits.event_clicked_link"),
    submitted_data: t("audits.event_submitted_data"),
  };
  return labels[eventType] || eventType;
};

// Méthode pour obtenir la sévérité du type d'événement
const getEventTypeSeverity = (eventType) => {
  const severities = {
    email_sent: "info",
    email_opened: "warn",
    clicked_link: "warn",
    submitted_data: "danger",
  };
  return severities[eventType] || "info";
};

// Méthode pour tronquer le user agent
const truncateUserAgent = (userAgent) => {
  if (!userAgent) return "N/A";
  return userAgent.length > 50 ? userAgent.substring(0, 50) + "..." : userAgent;
};

// Watch for data changes to redraw charts
watch(
  chartDataList,
  (newData) => {
    newData.forEach((item, index) => {
      if (canvasRefs.value[index]) {
        drawCircularProgress(canvasRefs.value[index], item);
      }
    });
  },
  { deep: true }
);
</script>

<style lang="scss" scoped>
@use "../../../assets/scss/variables.scss";
@use "../../../assets/scss/app/common.scss";

.timeline-card {
  @apply mx-2;
}
</style>
